import { useCallback } from 'react'
import { Signature, joinSignature } from '@ethersproject/bytes'
import { authApi } from 'restApi'
import { SignatureType, SignInMessageResponse } from 'restApi/openapi'
import { useSign, useTypedDataSign, useWagmi } from 'libs/wagmi'
import { USER_DENIED_SIGNATURE } from 'libs/wagmi/errors'
import store, { useAppDispatch } from 'state'
import { fetchSession } from 'state/profile'
import { StatusCode } from 'config/constants/httpStatusCodes'
import { REFERRER_LS_KEY } from 'config/constants/affiliate'
import { logAmplitudeEvent, logGtmEvent } from 'utils/analytics'

const useAuth = () => {
  const dispatch = useAppDispatch()
  const { account } = useWagmi()
  const { signMessage } = useSign()
  const { signTypedData } = useTypedDataSign()

  const linkWallet = useCallback(async () => {
    const storedReferrerId = window.localStorage.getItem(REFERRER_LS_KEY)?.replace(/\D/g, '')

    let signinMessage: SignInMessageResponse = null
    let signature: Signature = null
    let signatureVersion: SignatureType = null

    try {
      signinMessage = await authApi.getSignInMessage({
        referrerId: Number(storedReferrerId) || undefined,
        address: account,
      })
    } catch (error: any) {
      console.error(error)
    }

    try {
      const domain = { ...signinMessage.domain } as const
      const types = { ...signinMessage.types } as const
      const message = { ...signinMessage.message } as const

      // Get primaryType from types (should be the first key after EIP712Domain)
      const primaryType = Object.keys(types).find((key) => key !== 'EIP712Domain') || 'Message'

      signature = await signTypedData({ domain, types, primaryType, message })
      signatureVersion = SignatureType.ETH_SIGN_TYPED_DATA_V4
    } catch (error: any) {
      if (error?.code === USER_DENIED_SIGNATURE) {
        throw error
      }

      const { action, address, referrerId, timestamp } = signinMessage.message as any
      const message = `action=${action}&address=${address}&referrerId=${referrerId}&ts=${timestamp}`

      signature = await signMessage({ message })
      signatureVersion = SignatureType.PERSONAL_SIGN
    }

    try {
      const res = await authApi.signinRaw({
        signedSignInToNominexDomain: {
          ...signinMessage.message,
          signature: joinSignature(signature),
          signatureVersion,
        },
      })

      if (res.raw.status === StatusCode.CREATED) {
        const unsubscribe = store.subscribe(() => {
          const { address, referredBy, userId } = store.getState().profile.accountInfo

          if (referredBy) {
            logAmplitudeEvent('NomiswapSignup', { user_id: userId, referred_by: referredBy, wallet_address: address })
            logGtmEvent({ event: 'NomiswapSignup' })
            unsubscribe()
          }
        })
      }

      dispatch(fetchSession())
    } catch (error: any) {
      console.error(error)
    }
  }, [account, dispatch, signMessage, signTypedData])

  return { linkWallet }
}

export default useAuth
